/**
 * **Der Weg zu den ersten Werten** (HM-183, UI-Durchsicht U5).
 *
 * Bis dahin lag er über vier Menüs verteilt — Gerät koppeln (Geräteverwaltung),
 * Zählervorschlag bestätigen (Zähler), Vorlage wählen und Zähler stecken
 * (Messkonzept), Werte ansehen (Strom & Erzeugung) —, und keine der Seiten sagte,
 * was als Nächstes kommt. Die Liste fasst den Stand in vier Schritten zusammen und
 * zeigt nur für den nächsten offenen einen Weg.
 *
 * Gerechnet wird hier nichts Neues: Jeder Schritt ist eine Frage an eine Liste,
 * die die Oberfläche ohnehin lädt.
 */
import { FIRST_VALUES_DELAY } from "./first-values.js";

export type SetupStepKey = "device" | "meter" | "concept" | "values";

export interface SetupInput {
  /** Geräte an der Anlage (nicht freigegeben). */
  readonly devices: number;
  /** Zähler an der Anlage, ohne abgelöste und archivierte. */
  readonly meters: number;
  /** Offene Anlagevorschläge — das Gerät liefert, und ein Mensch soll bestätigen. */
  readonly openSuggestions: number;
  readonly concept: boolean;
  /** Ob für heute schon Werte vorliegen. */
  readonly values: boolean;
  /**
   * Ob ein Zähler des Konzepts überhaupt meldet (Momentanwerte). `false` heißt:
   * Warten hilft nicht, die Geräte liefern nichts — der Weg führt zu ihnen.
   * Weggelassen, wo es noch kein Konzept gibt und die Frage keinen Sinn hat.
   */
  readonly delivering?: boolean;
}

export interface SetupStep {
  readonly key: SetupStepKey;
  readonly title: string;
  readonly text: string;
  readonly done: boolean;
  /** Der erste offene Schritt — nur er bekommt einen Weg. */
  readonly current: boolean;
  /** Wohin der Weg führt; `null`, wo nur Warten hilft. */
  readonly action: { readonly label: string; readonly area: "devices" | "meters" | "concept" } | null;
}

export function setupSteps(input: SetupInput): SetupStep[] {
  const steps: Omit<SetupStep, "current">[] = [
    {
      key: "device",
      title: "Gerät verbinden",
      done: input.devices > 0,
      text:
        input.devices > 0
          ? input.devices === 1
            ? "Ein Gerät ist verbunden."
            : `${input.devices} Geräte sind verbunden.`
          : "Schalte am Lesekopf die Cloud-Anbindung ein. Er zeigt dann einen Code, den du hier eingibst.",
      action: { label: "Gerät hinzufügen", area: "devices" },
    },
    {
      key: "meter",
      title: "Zähler anlegen",
      done: input.meters > 0,
      text:
        input.meters > 0
          ? input.meters === 1
            ? "Ein Zähler ist angelegt."
            : `${input.meters} Zähler sind angelegt.`
          : input.openSuggestions > 0
            ? "Das Gerät liefert Werte. Bestätige, welche Zähler daraus werden."
            : "Sobald das Gerät die ersten Werte liefert, entstehen die Zähler — beim CTS-Lesekopf von selbst, bei anderen Geräten als Vorschlag zum Bestätigen.",
      action: input.openSuggestions > 0 ? { label: "Vorschläge ansehen", area: "meters" } : null,
    },
    {
      key: "concept",
      title: "Messkonzept wählen",
      done: input.concept,
      text: input.concept
        ? "Das Messkonzept ist eingerichtet."
        : "Lege fest, welcher Zähler das Netz misst und welcher die Erzeugung. Daraus entstehen Verbrauch, Eigenverbrauch und Autarkie.",
      action: { label: "Messkonzept wählen", area: "concept" },
    },
    {
      key: "values",
      title: "Erste Werte",
      done: input.values,
      text: input.values
        ? "Die ersten Werte sind da."
        : input.delivering === false
          ? "Noch meldet kein Zähler des Messkonzepts einen Wert. Sieh nach, ob die Geräte erreichbar sind."
          : FIRST_VALUES_DELAY,
      action: input.delivering === false ? { label: "Geräte ansehen", area: "devices" } : null,
    },
  ];
  const firstOpen = steps.findIndex((step) => !step.done);
  return steps.map((step, index) => ({ ...step, current: index === firstOpen }));
}

/** Ob die Anlage eingerichtet ist — dann verschwindet die Liste. */
export function setupComplete(input: SetupInput): boolean {
  return setupSteps(input).every((step) => step.done);
}
